import {
  DELIVERABLE_WORKFLOW,
  getCampaignDeliverableWorkflow,
  getPrimaryContentDeliverable,
} from '../shared/campaignContentDeliverables';

const DEFAULT_CONTENT_TYPE = 'content';

const PLATFORM_PLACEHOLDERS = {
  Instagram: 'https://www.instagram.com/p/...',
  TikTok: 'https://www.tiktok.com/@you/video/...',
  Whatnot: 'https://www.whatnot.com/live/...',
};

export function getContentLiveDeliverableContext(collaboration) {
  const deliverable = getPrimaryContentDeliverable(collaboration);
  const contentType = deliverable?.contentType || DEFAULT_CONTENT_TYPE;
  const platform = deliverable?.platform || collaboration?.platform_type || collaboration?.platformType || '';
  const workflow = getCampaignDeliverableWorkflow(collaboration);

  return {
    deliverable,
    contentType,
    platform,
    workflow,
    isLivestream: workflow === DELIVERABLE_WORKFLOW.LIVESTREAM,
    isStory: workflow === DELIVERABLE_WORKFLOW.STORY,
    isShortForm: workflow === DELIVERABLE_WORKFLOW.SHORT_FORM,
  };
}

export function getContentLiveSummary(collaboration, role) {
  const { contentType, isStory, isShortForm } = getContentLiveDeliverableContext(collaboration);

  if (role === 'brand') {
    if (isStory) return 'Review the creator\'s story link once it is live.';
    if (isShortForm) return 'Review the creator\'s published content link once it is live.';
    return 'Review and approve the creator\'s content once it\'s live.';
  }

  if (isStory) return 'Post your story with the campaign link, then submit the story link. The brand reviews before payout.';
  if (isShortForm) return `Post your ${contentType} and submit the published link. The brand reviews before payout.`;
  return 'Post, submit proof; the brand reviews before payout.';
}

export function getContentLiveLabel(collaboration, role, fallbackLabel) {
  return fallbackLabel;
}

export function getContentLiveStepContext({
  collaboration,
  role,
  partnerFirstName,
  deliverableSubmitted,
  fallbackTitle,
}) {
  const { contentType, isStory } = getContentLiveDeliverableContext(collaboration);

  if (role === 'brand') {
    if (deliverableSubmitted) {
      return {
        statusTitle: isStory ? 'Review submitted story' : 'Review submitted content',
        statusDescription: isStory
          ? `${partnerFirstName} submitted their ${contentType} link. Review and approve it when it is ready.`
          : `${partnerFirstName} submitted their ${contentType}. Review and approve it when it is ready.`,
      };
    }

    return {
      statusTitle: fallbackTitle,
      statusDescription: isStory
        ? `${partnerFirstName} will post their ${contentType} with your campaign link and submit it for review.`
        : `${partnerFirstName} will post their ${contentType} and submit the link for review.`,
    };
  }

  return {
    statusTitle: fallbackTitle,
    statusDescription: isStory
      ? `Post your ${contentType} with the campaign link, then submit your story link here.`
      : `Post your ${contentType} and submit the link to your published content.`,
  };
}

export function getContentLiveChipLabel(collaboration, role) {
  const { isStory } = getContentLiveDeliverableContext(collaboration);
  if (role === 'brand') {
    return isStory ? 'Waiting for story' : 'Waiting for content';
  }
  return isStory ? 'Add story link' : 'Submit link';
}

export function getContentLiveSectionTitle(collaboration) {
  const { contentType, isLivestream, isStory } = getContentLiveDeliverableContext(collaboration);
  if (isLivestream) return 'Live Stream';
  if (isStory) return 'Story';
  return contentType || 'Content Post';
}

export function getInfluencerContentLiveActionCopy(collaboration, status = 'pending') {
  const { contentType, isStory } = getContentLiveDeliverableContext(collaboration);

  if (isStory) {
    return {
      pendingDescription: 'Post the story with the campaign link, then add the public story link below for review.',
      rejectedDescription: 'Update the story based on the brand feedback, then resubmit the story link.',
      submitLabel: status === 'rejected' ? 'Resubmit Story Link' : 'Add Story Link',
    };
  }

  return {
    pendingDescription: `Post your ${contentType}, then submit the link to the published content for review.`,
    rejectedDescription: `Update your ${contentType} based on the brand feedback, then resubmit the link.`,
    submitLabel: status === 'rejected' ? 'Resubmit Link' : 'Submit Link',
  };
}

export function getSubmitContentModalCopy(collaboration) {
  const { contentType, platform, isStory } = getContentLiveDeliverableContext(collaboration);
  const placeholder = PLATFORM_PLACEHOLDERS[platform] || 'https://www.example.com/your-content';

  if (isStory) {
    return {
      title: 'Add Story Link',
      description: `Add the link to your published ${contentType}. Make sure the story includes the campaign link before submitting.`,
      submitLabel: 'Submit Story Link',
      urlLabel: 'Story URL',
      placeholder: platform === 'Instagram'
        ? 'https://www.instagram.com/stories/...'
        : placeholder,
      maxUrls: 1,
    };
  }

  return {
    title: 'Submit Link',
    description: `Add the link to your published ${contentType}. Use secure https:// links to public pages only.`,
    submitLabel: 'Submit Link',
    urlLabel: 'Content URL',
    placeholder,
    maxUrls: 20,
  };
}

export function getBrandContentLiveWaitingCopy(collaboration, creatorName = 'Creator') {
  const { contentType, isStory } = getContentLiveDeliverableContext(collaboration);
  if (isStory) {
    return `${creatorName} will post their ${contentType} with your campaign link and submit the story link for review.`;
  }
  return `${creatorName} will post their ${contentType} and submit the link for review.`;
}
